import { useState, useEffect } from 'react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  TrendingDown,
  Calendar,
  Plus,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import {
  reportsService,
  SavingsSnapshot,
  SavingsSummary,
  CreateSavingsSnapshotDto,
} from '../../services/reports.service';

export default function SavingsReportPage() {
  const user = useAuthStore((state) => state.user);
  const organizationId = user?.organizationId || '';

  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<SavingsSummary | null>(null);
  const [history, setHistory] = useState<SavingsSnapshot[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Date range for filtering
  const [startDate, setStartDate] = useState(() =>
    format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(() =>
    format(endOfMonth(new Date()), 'yyyy-MM-dd')
  );

  // Form state for adding new snapshot
  const [formData, setFormData] = useState({
    periodStart: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    periodEnd: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    baselineHeadcount: '',
    actualHeadcount: '',
    baselineOvertimeHours: '',
    actualOvertimeHours: '',
    hourlyRate: '25',
    overtimeMultiplier: '1.5',
    forecastAccuracy: '',
    schedulingEfficiency: '',
    notes: '',
  });

  const loadData = async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const [summaryData, historyData] = await Promise.all([
        reportsService.getSavingsSummary(organizationId, { startDate, endDate }),
        reportsService.getSavingsHistory(organizationId, { startDate, endDate }),
      ]);
      setSummary(summaryData);
      setHistory(historyData);
    } catch (error) {
      console.error('Failed to load savings data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [organizationId, startDate, endDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId) return;

    setIsSubmitting(true);
    try {
      const dto: CreateSavingsSnapshotDto = {
        organizationId,
        periodStart: formData.periodStart,
        periodEnd: formData.periodEnd,
        baselineHeadcount: parseFloat(formData.baselineHeadcount),
        actualHeadcount: parseFloat(formData.actualHeadcount),
        baselineOvertimeHours: parseFloat(formData.baselineOvertimeHours),
        actualOvertimeHours: parseFloat(formData.actualOvertimeHours),
        hourlyRate: parseFloat(formData.hourlyRate),
        overtimeMultiplier: parseFloat(formData.overtimeMultiplier),
        forecastAccuracy: formData.forecastAccuracy
          ? parseFloat(formData.forecastAccuracy)
          : undefined,
        schedulingEfficiency: formData.schedulingEfficiency
          ? parseFloat(formData.schedulingEfficiency)
          : undefined,
        notes: formData.notes || undefined,
      };

      await reportsService.createSnapshot(dto);
      setShowAddForm(false);
      setFormData({
        periodStart: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
        periodEnd: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
        baselineHeadcount: '',
        actualHeadcount: '',
        baselineOvertimeHours: '',
        actualOvertimeHours: '',
        hourlyRate: '25',
        overtimeMultiplier: '1.5',
        forecastAccuracy: '',
        schedulingEfficiency: '',
        notes: '',
      });
      loadData();
    } catch (error) {
      console.error('Failed to create snapshot:', error);
      alert('Failed to create savings snapshot');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (snapshotId: string) => {
    if (!confirm('Are you sure you want to delete this savings record?')) return;

    try {
      await reportsService.deleteSnapshot(snapshotId);
      loadData();
    } catch (error) {
      console.error('Failed to delete snapshot:', error);
      alert('Failed to delete savings record');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Savings Report</h1>
          <p className="text-gray-600">
            Track operating cost savings, headcount reduction, and overtime expense reduction
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => loadData()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Record
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <Calendar className="w-5 h-5 text-gray-500" />
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">Total Savings</span>
            </div>
            <div className="text-3xl font-bold text-green-600">
              {formatCurrency(summary.totalSavings)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {summary.snapshotCount} period{summary.snapshotCount !== 1 ? 's' : ''} tracked
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">Headcount Saved</span>
            </div>
            <div className="text-3xl font-bold text-blue-600">
              {summary.totalHeadcountSaved.toFixed(1)} FTE
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {formatCurrency(summary.totalLaborSavings)} labor savings
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-gray-600">Overtime Saved</span>
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {summary.totalOvertimeHoursSaved.toFixed(0)} hrs
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {formatCurrency(summary.totalOvertimeSavings)} overtime savings
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-600">Avg Forecast Accuracy</span>
            </div>
            <div className="text-3xl font-bold text-yellow-600">
              {summary.averageForecastAccuracy !== null
                ? `${summary.averageForecastAccuracy.toFixed(1)}%`
                : 'N/A'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {summary.averageSchedulingEfficiency !== null
                ? `${summary.averageSchedulingEfficiency.toFixed(1)}% scheduling efficiency`
                : 'No efficiency data'}
            </div>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4"
        >
          <h3 className="text-lg font-semibold text-gray-900">Add Savings Record</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period Start
              </label>
              <input
                type="date"
                value={formData.periodStart}
                onChange={(e) =>
                  setFormData({ ...formData, periodStart: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period End
              </label>
              <input
                type="date"
                value={formData.periodEnd}
                onChange={(e) =>
                  setFormData({ ...formData, periodEnd: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Baseline Headcount (FTE)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.baselineHeadcount}
                onChange={(e) =>
                  setFormData({ ...formData, baselineHeadcount: e.target.value })
                }
                placeholder="What headcount would be without WFM"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Headcount (FTE)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.actualHeadcount}
                onChange={(e) =>
                  setFormData({ ...formData, actualHeadcount: e.target.value })
                }
                placeholder="Actual headcount used"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Baseline Overtime Hours
              </label>
              <input
                type="number"
                value={formData.baselineOvertimeHours}
                onChange={(e) =>
                  setFormData({ ...formData, baselineOvertimeHours: e.target.value })
                }
                placeholder="Expected OT without WFM"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Overtime Hours
              </label>
              <input
                type="number"
                value={formData.actualOvertimeHours}
                onChange={(e) =>
                  setFormData({ ...formData, actualOvertimeHours: e.target.value })
                }
                placeholder="Actual OT used"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.hourlyRate}
                onChange={(e) =>
                  setFormData({ ...formData, hourlyRate: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overtime Multiplier
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.overtimeMultiplier}
                onChange={(e) =>
                  setFormData({ ...formData, overtimeMultiplier: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forecast Accuracy (%) - Optional
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.forecastAccuracy}
                onChange={(e) =>
                  setFormData({ ...formData, forecastAccuracy: e.target.value })
                }
                placeholder="e.g., 92.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheduling Efficiency (%) - Optional
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.schedulingEfficiency}
                onChange={(e) =>
                  setFormData({ ...formData, schedulingEfficiency: e.target.value })
                }
                placeholder="e.g., 95.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about this period"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Record'}
            </button>
          </div>
        </form>
      )}

      {/* History Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Savings History</h3>
        </div>

        {history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No savings records found for the selected period.
            <br />
            Click "Add Record" to track your first savings snapshot.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Headcount Saved
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    OT Hours Saved
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Total Savings
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Accuracy
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((snapshot) => (
                  <tr key={snapshot.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {format(new Date(snapshot.periodStart), 'MMM d, yyyy')} -{' '}
                      {format(new Date(snapshot.periodEnd), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`flex items-center ${
                          snapshot.headcountSaved >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {snapshot.headcountSaved >= 0 ? (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingUp className="w-4 h-4 mr-1" />
                        )}
                        {Math.abs(snapshot.headcountSaved).toFixed(1)} FTE
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`flex items-center ${
                          snapshot.overtimeHoursSaved >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {snapshot.overtimeHoursSaved >= 0 ? (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingUp className="w-4 h-4 mr-1" />
                        )}
                        {Math.abs(snapshot.overtimeHoursSaved).toFixed(0)} hrs
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">
                      {formatCurrency(snapshot.totalSavings)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {snapshot.forecastAccuracy !== null
                        ? `${snapshot.forecastAccuracy.toFixed(1)}%`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(snapshot.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
